import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAdmin } from '../contexts/AdminContext';
import AdminSidebar from './AdminSidebar';
import AdminNavbar from './AdminNavbar';
import { Loader2, ArrowLeft } from 'lucide-react';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title }) => {
  const { currentUser } = useAuth();
  const { isAdmin, isLoading, error } = useAdmin();

  // If not logged in, redirect to login
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Verifying admin access...</p>
      </div>
    );
  }

  // If not an admin, show access denied
  if (!isAdmin) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center">
        <div className="bg-card p-8 rounded-lg shadow-lg max-w-md text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You do not have permission to access the admin area.
          </p>
          <p className="text-sm text-destructive mb-6">
            {error || 'Please contact the administrator if you believe this is an error.'}
          </p>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  // Admin layout
  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <AdminSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <AdminNavbar title={title} />
        <main className="flex-1 overflow-auto p-6 pb-6">
          {/* Back button to landing page */}
          <div className="flex justify-start mb-4">
            <Link
              to="/"
              className="flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to Landing Page
            </Link>
          </div>
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
